import React, { useState } from 'react';
import { TabsContent } from '../ui/tabs';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { behavioralAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Brain, Send, MessageSquareQuote, Copy, ExternalLink } from 'lucide-react';
import { BarGraph, DivergingGraph, TRAIT_COLORS } from '../behavioral/DiscGraphs';

const TERRA = '#0076A3';

function FitBadge({ fit }) {
  if (!fit) return null;
  const col = fit.band === 'Strong' ? '#059669' : fit.band === 'Moderate' ? '#D97706' : '#DC2626';
  const bg = fit.band === 'Strong' ? '#F0FDF4' : fit.band === 'Moderate' ? '#FFFBEB' : '#FEF2F2';
  return (
    <div data-testid="candidate-behavioral-fit" style={{ background: bg, border: `1px solid ${col}33`, borderRadius: 12, padding: '1rem', marginTop: '1rem', display: 'flex', gap: 14, alignItems: 'center' }}>
      <div style={{ textAlign: 'center', flexShrink: 0 }}>
        <div style={{ fontFamily: 'Cabinet Grotesk', fontSize: '1.9rem', fontWeight: 800, color: col, lineHeight: 1 }}>{Math.round(fit.score)}</div>
        <div style={{ fontSize: '0.62rem', color: col, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role fit</div>
      </div>
      <div>
        <div style={{ fontWeight: 700, color: col, fontSize: '0.85rem' }}>{fit.band} fit for this role</div>
        <div style={{ fontSize: '0.8rem', color: '#57534E', lineHeight: 1.5, marginTop: 2 }}>{fit.summary.replace(/^(Strong|Moderate|Weak) fit\.\s*/, '')}</div>
      </div>
    </div>
  );
}

export default function CandidateBehavioralTab({ candidate, candidateId, parsed, loadCandidate }) {
  const navigate = useNavigate();
  const bh = candidate.behavioral || { status: 'none' };
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState(parsed?.email || '');
  const [link, setLink] = useState('');

  const invite = async () => {
    if (!email.trim() || !email.includes('@')) { toast.error('A valid candidate email is required'); return; }
    setInviting(true);
    try {
      const { data } = await behavioralAPI.invite({
        candidate_name: candidate.name,
        candidate_email: email.trim(),
        candidate_id: candidateId,
        job_id: candidate.job_id || undefined,
      });
      setLink(data.link);
      toast.success(data.email_sent ? 'Psychometric invite emailed' : 'Invite created — copy the link');
      loadCandidate && loadCandidate();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create invite');
    } finally {
      setInviting(false);
    }
  };

  const copy = (l) => navigator.clipboard?.writeText(l).then(() => toast.success('Link copied')).catch(() => {});

  return (
    <TabsContent value="behavioral">
      {link ? (
        <Card data-testid="candidate-behavioral-invite-success" style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.75rem', textAlign: 'center' }}>
          <Send size={30} color={TERRA} style={{ margin: '0 auto 0.6rem' }} />
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontWeight: 700, color: '#002A38', fontSize: '1rem' }}>Invite created</h3>
          <p style={{ color: '#78716C', fontSize: '0.82rem', margin: '0.35rem auto 1rem', maxWidth: 420 }}>We emailed {candidate.name?.split(' ')[0] || 'the candidate'} a link. You can also copy it here:</p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', background: '#EDF4F7', borderRadius: 10, padding: '0.6rem 0.75rem', maxWidth: 460, margin: '0 auto 1rem' }}>
            <span style={{ flex: 1, fontSize: '0.78rem', color: '#44403C', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{link}</span>
            <button data-testid="candidate-behavioral-copy" onClick={() => copy(link)} style={{ border: '1px solid #E7E5E4', background: '#fff', borderRadius: 8, width: 32, height: 32, cursor: 'pointer' }}><Copy size={15} /></button>
          </div>
          <Button data-testid="candidate-behavioral-invite-done" variant="outline" onClick={() => setLink('')}>Done</Button>
        </Card>
      ) : bh.status === 'completed' ? (() => {
        const p = bh.profile || {};
        const probes = (p.recruiter?.interview_probes || []);
        return (
          <div data-testid="candidate-behavioral-completed" style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ width: 42, height: 42, borderRadius: 10, background: TRAIT_COLORS[p.primary_style], color: '#fff', fontWeight: 800, fontSize: '1.1rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.primary_style}</span>
                  <div>
                    <div style={{ fontFamily: 'Cabinet Grotesk', fontWeight: 700, color: '#002A38', fontSize: '1.05rem' }}>{p.style_label}</div>
                    <div style={{ fontSize: '0.75rem', color: '#78716C' }}>Psychometric profile{bh.completed_at ? ` · ${new Date(bh.completed_at).toLocaleDateString()}` : ''}</div>
                  </div>
                </div>
                <Button data-testid="candidate-behavioral-view-full" variant="outline" onClick={() => navigate(`/behavioral-assessments/${bh.session_id}`)} style={{ borderColor: TERRA, color: TERRA }}>
                  <ExternalLink size={15} style={{ marginRight: 6 }} /> Full report
                </Button>
              </div>
              <FitBadge fit={bh.fit} />
            </Card>

            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,1fr) minmax(0,1fr)', gap: '1rem' }} className="cand-bh-grid">
              <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem' }}>
                <BarGraph title="Public Self (Graph I)" data={p.graph1} testid="cand-bh-g1" />
                <DivergingGraph title="Adapted Style (Graph III)" data={p.graph3} testid="cand-bh-g3" />
              </Card>
              <Card style={{ background: '#fff', border: '1px solid #ECE7DE', borderRadius: 14, padding: '1.25rem' }}>
                <div style={{ fontFamily: 'Cabinet Grotesk', fontWeight: 700, color: '#002A38', display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                  <MessageSquareQuote size={17} color={TERRA} /> Interview probes
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {probes.map((pr) => (
                    <div key={pr.question} style={{ padding: '0.75rem 0.9rem', background: '#FAF7F2', borderRadius: 10, borderLeft: `3px solid ${TERRA}` }}>
                      <div style={{ fontSize: '0.64rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: TERRA, marginBottom: 3 }}>{pr.focus}</div>
                      <div style={{ fontSize: '0.82rem', color: '#002A38', lineHeight: 1.45 }}>{pr.question}</div>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        );
      })() : bh.status === 'invited' || bh.status === 'in_progress' ? (
        <Card data-testid="candidate-behavioral-pending" style={{ background: '#FFFBEB', border: '1px solid #FDE68A', borderRadius: 14, padding: '1.5rem', display: 'flex', alignItems: 'center', gap: 14 }}>
          <span className="status-dot status-processing" style={{ width: 14, height: 14 }} />
          <div>
            <div style={{ fontWeight: 700, color: '#B45309' }}>Psychometric assessment {bh.status === 'in_progress' ? 'in progress' : 'invited'}</div>
            <div style={{ fontSize: '0.82rem', color: '#92702A' }}>
              {bh.status === 'in_progress' ? `${bh.answered_count}/${bh.total_questions} answered — ` : 'Awaiting completion — '}
              the profile will appear here once {candidate.name?.split(' ')[0] || 'the candidate'} finishes.
            </div>
          </div>
        </Card>
      ) : (
        <Card data-testid="candidate-behavioral-invite" style={{ background: '#fff', border: '1px dashed #ECE7DE', borderRadius: 14, padding: '2rem', textAlign: 'center' }}>
          <Brain size={38} color="#D6CFC6" style={{ margin: '0 auto 0.75rem' }} />
          <h3 style={{ fontFamily: 'Cabinet Grotesk', fontWeight: 700, color: '#002A38', fontSize: '1.05rem' }}>No psychometric profile yet</h3>
          <p style={{ color: '#78716C', fontSize: '0.85rem', margin: '0.4rem auto 1.25rem', maxWidth: 420 }}>
            Send {candidate.name?.split(' ')[0] || 'this candidate'} a short (8–10 min) DISC-style assessment. Results link back here automatically.
          </p>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', flexWrap: 'wrap' }}>
            <input data-testid="candidate-behavioral-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="candidate@email.com"
              style={{ border: '1px solid #E7E5E4', borderRadius: 8, padding: '0.55rem 0.7rem', fontSize: '0.85rem', width: 240 }} />
            <Button data-testid="candidate-behavioral-invite-btn" onClick={invite} disabled={inviting} style={{ background: TERRA, color: '#fff' }}>
              <Send size={15} style={{ marginRight: 6 }} /> {inviting ? 'Sending…' : 'Invite to assessment'}
            </Button>
          </div>
        </Card>
      )}
      <style>{`@media (max-width: 820px){ .cand-bh-grid{ grid-template-columns: minmax(0,1fr) !important; } }`}</style>
    </TabsContent>
  );
}
